import React from "react";
import { Skeleton } from "./LoadingStates";
import { useCachedRequest } from "../../helpers/cachedFetch";
import { getVisitorSources } from "../../helpers/fakebackend_helper";

const SOURCE_LABEL = {
  x: "X", tiktok: "TikTok", youtube: "YouTube", meta: "Meta", reddit: "Reddit",
  telegram: "Telegram", discord: "Discord", linkedin: "LinkedIn",
  organic: "Search (organic)", internal: "Our own sites", referral: "Other referral",
  direct: "Direct / untagged", unknown: "Before tracking",
};
const SOURCE_COLOR = {
  x: "#5758fc", tiktok: "#57c7ff", youtube: "#ff5d6c", meta: "#4a7cf7",
  reddit: "#f3af4c", telegram: "#57c7ff", discord: "#7289da", linkedin: "#0a66c2",
  organic: "#2BD56F", internal: "#9aa4bf", referral: "#b57cf7",
  direct: "#6b7488", unknown: "#4a5162",
};

/**
 * First-touch traffic-source split for biorlabs.com.
 *
 * Counts VISITORS, not pageloads, so it sums to the same denominator as the
 * "Unique visitors" card. Rows recorded before tracking shipped show as
 * "Before tracking" rather than being hidden — hiding them would make the split
 * silently disagree with the headline total.
 */
const TrafficSources = ({ days }) => {
  const { data, loading } = useCachedRequest({
    key: `visitor:sources:${days || "all"}`,
    fetcher: () => getVisitorSources(days).catch(() => null),
    deps: [days],
  });

  if (loading) return <Skeleton height={120} radius={8} />;
  if (!data || !data.sources) {
    return (
      <div style={{ padding: "1rem", color: "var(--bl-text-soft)", fontSize: 13, textAlign: "center" }}>
        Awaiting backend — GET /visitors/sources
      </div>
    );
  }

  const rows = data.sources.filter((r) => r.visitors > 0);
  const untagged = rows.find((r) => r.source === "direct");
  const unknown = rows.find((r) => r.source === "unknown");

  if (rows.length === 0) {
    return <div style={{ padding: "1rem", color: "var(--bl-text-soft)", fontSize: 13 }}>No visitors recorded yet.</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginBottom: 12 }}>
        {rows.map((r) => (
          <div
            key={r.source}
            title={`${SOURCE_LABEL[r.source] || r.source}: ${r.visitors}`}
            style={{ width: `${Math.max(r.share * 100, 0.5)}%`, background: SOURCE_COLOR[r.source] || "#6b7488" }}
          />
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {rows.map((r) => (
          <div key={r.source} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: SOURCE_COLOR[r.source] || "#6b7488", flexShrink: 0 }} />
            <span style={{ flex: 1, color: "var(--bl-text-strong)" }}>{SOURCE_LABEL[r.source] || r.source}</span>
            <span style={{ fontFamily: "var(--bl-font-numeric)", color: "var(--bl-text-soft)" }}>
              {Math.round(r.share * 100)}%
            </span>
            <span style={{ fontFamily: "var(--bl-font-numeric)", minWidth: 48, textAlign: "right" }}>{r.visitors}</span>
          </div>
        ))}
      </div>

      {data.campaigns && data.campaigns.length > 0 && (
        <div style={{ marginTop: 12, borderTop: "1px solid var(--bl-line)", paddingTop: 8 }}>
          <div style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Top campaigns
          </div>
          {data.campaigns.slice(0, 6).map((c) => (
            <div key={c.campaign} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0" }}>
              <span style={{ color: "var(--bl-text-strong)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.campaign}</span>
              <span style={{ fontFamily: "var(--bl-font-numeric)", color: "var(--bl-text-soft)" }}>{c.visitors}</span>
            </div>
          ))}
        </div>
      )}

      {/* Say out loud why "direct" may be large, so nobody reads it as apathy. */}
      {(untagged || unknown) && (
        <div style={{ marginTop: 10, fontSize: 11, color: "var(--bl-text-soft)", lineHeight: 1.5 }}>
          {unknown ? `${unknown.visitors} visitor${unknown.visitors === 1 ? "" : "s"} predate source tracking. ` : ""}
          {untagged ? "“Direct / untagged” also collects traffic whose referrer the browser stripped — X and TikTok in-app browsers do this. Use tagged links (utm_source) to pull it apart." : ""}
        </div>
      )}
    </div>
  );
};

export default TrafficSources;
